import FullPageTypeahead from '@/components/full-page-typeahead'
import { Drawer, DrawerContent, DrawerHeader } from '@/components/mobile-drawer'
import { SpecialtyType } from '@/lib/generated/graphql-types'
import {
  SpecialtyOption,
  useSpecialtySearch,
} from '@/modules/salary/filters/use-specialty-search'
import { Controller, useFormContext } from 'react-hook-form'
import { SalaryFilterControls } from '@/modules/salary/types'
import { SalaryFilterControl } from '../../constants'
import { useEffect } from 'react'

export function SpecialtyDrawer({
  isOpen,
  onClose,
  selectedSpecialty,
  professionSlug,
  isAuthenticated,
}: {
  isOpen: boolean
  onClose: () => void
  selectedSpecialty: SpecialtyType | null
  professionSlug: string | null | undefined
  isAuthenticated: boolean
}) {
  const { control, setValue, watch } = useFormContext<SalaryFilterControls>()
  const {
    searchSpecialty,
    options,
    loading: specialtiesLoading,
  } = useSpecialtySearch({
    overrideProfessionSlug: professionSlug,
    isAuthenticated,
  })
  const selectedSpecialtySlug = watch(SalaryFilterControl.specialty)

  useEffect(() => {
    if (specialtiesLoading) return
    // clear specialty if not found in options, eg.
    // when the professionId is changed
    const selectedSpecialtyOption = options.find(
      (option) => option.slug === selectedSpecialtySlug,
    )
    if (!selectedSpecialtyOption && options.length) {
      setValue(SalaryFilterControl.specialty, '')
    }
  }, [options, selectedSpecialtySlug, setValue, specialtiesLoading])

  useEffect(() => {
    const onlyOptionSlug = options?.[0]?.slug
    if (
      options.length === 1 &&
      onlyOptionSlug &&
      onlyOptionSlug !== selectedSpecialtySlug
    ) {
      setValue(SalaryFilterControl.specialty, onlyOptionSlug)
    }
  }, [options, setValue, selectedSpecialtySlug])

  return (
    <Drawer
      snapPoints={[1]}
      fadeFromIndex={0}
      onClose={onClose}
      open={isOpen}
      direction="right"
    >
      <DrawerContent>
        <DrawerHeader>Specialty</DrawerHeader>
        <div className="p-4 flex flex-col grow">
          <Controller
            render={({ field: { onChange } }) => {
              return (
                <FullPageTypeahead<SpecialtyOption>
                  name={SalaryFilterControl.specialty}
                  searchOptions={searchSpecialty}
                  defaultQuery={selectedSpecialty?.fullName ?? ''}
                  onClear={() => onChange(null)}
                  onSelect={(option) => {
                    onChange(option.slug)
                    onClose()
                  }}
                  placeholder="Search specialties"
                  debounceTimer={0}
                />
              )
            }}
            name={SalaryFilterControl.specialty}
            control={control}
          />
        </div>
      </DrawerContent>
    </Drawer>
  )
}
